import { linkPlayer, unlinkPlayer, type AccountView, type PlayerView } from '../../api/admin';
import { ca } from '../../i18n/ca';
import { Avatar } from '../IdentityPanel';
import { errorText, fmtDay, fmtWhen } from './shared';

interface Props {
  players: PlayerView[];
  accounts: AccountView[];
  onPlayers: (list: PlayerView[]) => void;
  onError: (text: string | null) => void;
}

/** Players tab: the Steam ids the game saw, each linked (or not) to an account. */
export function PlayersTab({ players, accounts, onPlayers, onError }: Props) {
  function apply(call: Promise<PlayerView[]>) {
    call
      .then((list) => {
        onPlayers(list);
        onError(null);
      })
      .catch((err) => onError(errorText(err)));
  }

  return (
    <section className="stack" aria-label={ca.gamePlayers}>
      <p className="muted small">{ca.adminUi.intros.players}</p>
      {players.length === 0 ? (
        <div className="empty">
          <i className="bi bi-controller" aria-hidden="true" />
          <p>{ca.noPlayersYet}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data players">
            <thead>
              <tr>
                <th>{ca.adminUi.colPlayer}</th>
                <th>{ca.adminUi.colUid}</th>
                <th>{ca.adminUi.colLastSeen}</th>
                <th>{ca.adminUi.colLastPlayed}</th>
                <th>{ca.adminUi.colTimes}</th>
                <th>{ca.adminUi.colLinkedAccount}</th>
                <th>{ca.adminUi.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.uid} aria-label={p.name}>
                  <td>
                    <span className="row who">
                      <Avatar url={p.avatarUrl} name={p.name} size={28} />
                      <strong>{p.name}</strong>
                    </span>
                  </td>
                  <td>
                    <code className="uid">{p.uid}</code>
                  </td>
                  <td className="muted small nowrap">{p.lastSeen ? fmtWhen(p.lastSeen) : '—'}</td>
                  <td className="muted small nowrap">
                    {p.lastPlayedAt ? fmtDay(p.lastPlayedAt) : '—'}
                  </td>
                  <td className="num">{p.seenCount}</td>
                  <td>
                    {p.handle ? (
                      <span className="row">
                        @{p.handle}
                        <span className={`chip ${p.verified ? 'ok' : 'warn'}`}>
                          {p.verified ? ca.identityVerified : ca.identityClaimed}
                        </span>
                      </span>
                    ) : (
                      <span className="muted small">{ca.adminUi.noLinkedAccount}</span>
                    )}
                  </td>
                  <td>
                    {p.handle ? (
                      <button
                        type="button"
                        className="small"
                        onClick={() => apply(unlinkPlayer(p.uid))}
                      >
                        {ca.unlinkPlayer}
                      </button>
                    ) : (
                      <select
                        className="control"
                        aria-label={ca.linkToAccount}
                        value=""
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          if (id) apply(linkPlayer(p.uid, id));
                        }}
                      >
                        <option value="">{ca.linkToAccount}</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            @{a.handle}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
